import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe, logout as logoutApi } from '../api/auth';
import { getAccounts } from '../api/account';
import { getStrategyStatus, setAutoTrade, manualCheck, getComparison } from '../api/strategy';
import Sidebar from '../components/Sidebar';
import CollectingOverlay from '../components/CollectingOverlay';

function Strategy() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  // StockEasy 상태
  const [isAutoTrading, setIsAutoTrading] = useState(false);
  const [stockeasyHoldings, setStockeasyHoldings] = useState([]);
  const [stockeasyExits, setStockeasyExits] = useState([]);
  const [stockeasyMetadata, setStockeasyMetadata] = useState({});
  const [lastCheckTime, setLastCheckTime] = useState(null);
  const [lastUpdateDetected, setLastUpdateDetected] = useState(null);
  const [todayNewEntries, setTodayNewEntries] = useState([]);
  const [todayBuyLog, setTodayBuyLog] = useState([]);
  const [checkCountToday, setCheckCountToday] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [collectState, setCollectState] = useState(null);

  // UI 상태
  const [dataLoading, setDataLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [activeTab, setActiveTab] = useState('holdings');

  useEffect(() => {
    const init = async () => {
      try {
        const userData = await getMe();
        setUser(userData);

        const accountsData = await getAccounts();
        setAccounts(accountsData.accounts);

        if (accountsData.accounts.length > 0) {
          setSelectedAccount(accountsData.accounts[0]);
        }
      } catch (err) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  const fetchStatus = useCallback(async () => {
    setDataLoading(true);
    try {
      const status = await getStrategyStatus();
      setIsAutoTrading(status.auto_trade_enabled);
      setStockeasyHoldings(status.current_holdings || []);
      setStockeasyExits(status.current_exits || []);
      setStockeasyMetadata(status.metadata || {});
      setLastCheckTime(status.last_check_time);
      setLastUpdateDetected(status.last_update_detected);
      setTodayNewEntries(status.today_new_entries || []);
      setTodayBuyLog(status.today_buy_log || []);
      setCheckCountToday(status.check_count_today || 0);
      setIsChecking(status.is_checking || false);
      setCollectState(status.collect_state || null);
    } catch (err) {
      console.error('상태 조회 실패:', err);
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchStatus();
    }
  }, [loading, fetchStatus]);

  // 자동 새로고침: 15:25~15:30은 5초, 나머지 30초
  useEffect(() => {
    const getInterval = () => {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      if (h === 15 && m >= 25 && m < 30) return 5000;
      return 30000;
    };

    let timer;
    const scheduleNext = () => {
      timer = setTimeout(() => {
        fetchStatus();
        scheduleNext();
      }, getInterval());
    };
    scheduleNext();

    return () => clearTimeout(timer);
  }, [fetchStatus]);

  const handleAutoTradeToggle = async () => {
    if (!selectedAccount) {
      alert('먼저 계좌를 선택해주세요');
      return;
    }

    try {
      const newState = !isAutoTrading;
      await setAutoTrade(newState, selectedAccount.id);
      setIsAutoTrading(newState);
    } catch (err) {
      alert('자동매매 설정 실패: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleManualCheck = async () => {
    setChecking(true);
    try {
      await manualCheck();
      await fetchStatus();
    } catch (err) {
      alert('체크 실패: ' + (err.response?.data?.detail || err.message));
    } finally {
      setChecking(false);
    }
  };

  const handleFetchComparison = async () => {
    try {
      const data = await getComparison();
      setComparison(data);
      setShowComparison(true);
    } catch (err) {
      alert('비교 조회 실패: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleLogout = () => {
    logoutApi();
    navigate('/login');
  };

  const formatTime = (dateStr) => {
    if (!dateStr || dateStr === 'None') return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatMoney = (amount) => {
    if (!amount) return '-';
    return amount.toLocaleString('ko-KR');
  };

  if (loading) {
    return (
      <CollectingOverlay>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </CollectingOverlay>
    );
  }

  return (
  <CollectingOverlay>
    <div className="min-h-screen bg-gray-50">
      <Sidebar user={user} onLogout={handleLogout} />

      <main className="p-4 md:p-8 pt-20 md:pt-24 w-full max-w-6xl mx-auto">
        <header className="flex justify-between items-center mb-3 md:mb-4">
          <div>
            <h1 className="text-base md:text-2xl font-bold text-gray-800">전략 실행</h1>
            <p className="text-xs text-gray-500">StockEasy 카피 트레이딩</p>
          </div>
          <div className="flex items-center space-x-4">
            {accounts.length > 0 && (
              <select
                value={selectedAccount?.id || ''}
                onChange={(e) => {
                  const account = accounts.find(a => a.id === e.target.value);
                  setSelectedAccount(account);
                }}
                className="px-2 py-1 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none"
              >
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.account_no}
                  </option>
                ))}
              </select>
            )}
          </div>
        </header>

        <div className="space-y-2">
          {/* 자동매매 컨트롤 */}
          <div className="bg-white rounded-xl shadow-sm p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {isAutoTrading && (
                  <div className="relative">
                    <div className="w-8 h-8 border-3 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    </div>
                  </div>
                )}
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-sm text-gray-500 font-medium">자동매매</h2>
                    {isAutoTrading && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-600 rounded-full animate-pulse">
                        운영 중
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {isAutoTrading
                      ? '15:14 청산, 15:25~15:30 카피 매수'
                      : '자동매매 비활성화됨'}
                  </p>
                </div>
              </div>

              <button
                onClick={handleAutoTradeToggle}
                className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                  isAutoTrading ? 'bg-primary-500' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${
                    isAutoTrading ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* 상태 카드 그리드 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
              <span className="text-sm text-gray-500 font-medium">SE 보유</span>
              <span className="text-sm font-bold text-primary-500 tabular-nums">{stockeasyHoldings.length}개</span>
            </div>
            <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
              <span className="text-sm text-gray-500 font-medium">오늘 신규</span>
              <span className={`text-sm font-bold tabular-nums ${todayNewEntries.length > 0 ? 'text-red-500' : 'text-gray-800'}`}>
                {todayNewEntries.length}개
              </span>
            </div>
            <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
              <span className="text-sm text-gray-500 font-medium">마지막 체크</span>
              <span className="text-sm font-bold text-gray-800 tabular-nums">{formatTime(lastCheckTime)}</span>
            </div>
            <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
              <span className="text-sm text-gray-500 font-medium">데이터 갱신</span>
              <span className="text-sm font-bold text-gray-800 tabular-nums">{formatTime(lastUpdateDetected)}</span>
            </div>
          </div>

          {/* 액션 버튼 */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={fetchStatus}
              disabled={dataLoading}
              className="bg-white rounded-lg shadow-sm px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition disabled:opacity-50"
            >
              {dataLoading ? '조회 중...' : '새로고침'}
            </button>
            <button
              onClick={handleManualCheck}
              disabled={checking}
              className="bg-primary-50 rounded-lg shadow-sm px-3 py-2 text-sm text-primary-600 hover:bg-primary-100 transition disabled:opacity-50"
            >
              {checking ? '체크 중...' : '수동 체크'}
            </button>
            <button
              onClick={handleFetchComparison}
              className="bg-blue-50 rounded-lg shadow-sm px-3 py-2 text-sm text-blue-600 hover:bg-blue-100 transition"
            >
              보유 비교
            </button>
          </div>

          {/* 스케줄 + 체크 상태 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className="bg-white rounded-xl shadow-sm p-3">
              <h3 className="text-sm text-gray-500 font-medium mb-2">스케줄</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                  <div>
                    <p className="text-xs font-medium text-gray-700">15:14</p>
                    <p className="text-xs text-gray-500">청산 체크 (20일선)</p>
                  </div>
                  <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">자동</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                  <div>
                    <p className="text-xs font-medium text-gray-700">15:25~15:30</p>
                    <p className="text-xs text-gray-500">StockEasy 체크 (10초마다)</p>
                  </div>
                  <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">카피</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                  <div>
                    <p className="text-xs font-medium text-gray-700">실시간</p>
                    <p className="text-xs text-gray-500">손절 (-8%)</p>
                  </div>
                  <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">자동</span>
                </div>
              </div>
            </div>

            {/* 체크 상태 */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <h3 className="text-sm text-gray-500 font-medium mb-2">StockEasy 정보</h3>
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">target_date</span>
                  <span className="font-medium text-gray-700">{stockeasyMetadata.target_date || '-'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">updated_at</span>
                  <span className="font-medium text-gray-700">{stockeasyMetadata.updated_at ? stockeasyMetadata.updated_at.split('T')[1]?.split('.')[0] : '-'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">오늘 체크 횟수</span>
                  <span className="font-medium text-gray-700">{checkCountToday}회</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">이탈 종목</span>
                  <span className="font-medium text-gray-700">{stockeasyExits.length}개</span>
                </div>
                {isChecking && (
                  <div className="flex items-center space-x-2 mt-2 p-2 bg-blue-50 rounded-lg">
                    <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs text-blue-600 font-medium">체크 중...</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 탭 */}
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            {[
              { key: 'holdings', label: 'SE 보유종목', count: stockeasyHoldings.length },
              { key: 'exits', label: '이탈종목', count: stockeasyExits.length },
              { key: 'new', label: '오늘 신규', count: todayNewEntries.length },
              { key: 'log', label: '매수 로그', count: todayBuyLog.length },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition ${
                  activeTab === tab.key
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label} {tab.count > 0 && <span className="text-primary-500">({tab.count})</span>}
              </button>
            ))}
          </div>

          {/* 탭 콘텐츠 */}
          <div className="bg-white rounded-xl shadow-sm p-3">
            {/* SE 보유종목 */}
            {activeTab === 'holdings' && (
              stockeasyHoldings.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">종목</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">매수가</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">현재가</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">수익률</th>
                        <th className="hidden md:table-cell text-right py-2 px-2 text-xs font-medium text-gray-500">보유일</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockeasyHoldings.map((stock) => (
                        <tr key={stock.stock_code} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 px-2">
                            <p className="font-medium text-gray-800 text-xs">{stock.stock_name}</p>
                            <p className="text-xs text-gray-400">{stock.stock_code}</p>
                          </td>
                          <td className="py-2 px-2 text-right text-xs text-gray-600 tabular-nums">
                            {formatMoney(stock.buy_price)}
                          </td>
                          <td className="py-2 px-2 text-right text-xs font-medium text-gray-800 tabular-nums">
                            {formatMoney(stock.current_price)}
                          </td>
                          <td className={`py-2 px-2 text-right font-medium text-xs tabular-nums ${
                            stock.return_rate >= 0 ? 'text-red-500' : 'text-blue-500'
                          }`}>
                            {stock.return_rate >= 0 ? '+' : ''}{stock.return_rate.toFixed(2)}%
                          </td>
                          <td className="hidden md:table-cell py-2 px-2 text-right text-xs text-gray-600 tabular-nums">
                            {stock.holding_days === 0 ? '진입' : `${stock.holding_days}일`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">StockEasy 보유종목 데이터 없음</p>
                  <p className="text-xs mt-1">"수동 체크"를 눌러 데이터를 가져오세요</p>
                </div>
              )
            )}

            {/* 이탈종목 */}
            {activeTab === 'exits' && (
              stockeasyExits.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">종목</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">매수가</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">매도가</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">수익률</th>
                        <th className="hidden md:table-cell text-right py-2 px-2 text-xs font-medium text-gray-500">보유일</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockeasyExits.map((stock) => (
                        <tr key={stock.stock_code} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 px-2">
                            <p className="font-medium text-gray-800 text-xs">{stock.stock_name}</p>
                            <p className="text-xs text-gray-400">{stock.stock_code}</p>
                          </td>
                          <td className="py-2 px-2 text-right text-xs text-gray-600 tabular-nums">
                            {formatMoney(stock.buy_price)}
                          </td>
                          <td className="py-2 px-2 text-right text-xs font-medium text-gray-800 tabular-nums">
                            {formatMoney(stock.sell_price)}
                          </td>
                          <td className={`py-2 px-2 text-right font-medium text-xs tabular-nums ${
                            stock.return_rate >= 0 ? 'text-red-500' : 'text-blue-500'
                          }`}>
                            {stock.return_rate >= 0 ? '+' : ''}{stock.return_rate.toFixed(2)}%
                          </td>
                          <td className="hidden md:table-cell py-2 px-2 text-right text-xs text-gray-600 tabular-nums">
                            {stock.holding_days}일
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">오늘 이탈 종목 없음</p>
                </div>
              )
            )}

            {/* 오늘 신규 */}
            {activeTab === 'new' && (
              todayNewEntries.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">종목</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">매수가</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">현재가</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todayNewEntries.map((stock, idx) => (
                        <tr key={`${stock.stock_code}-${idx}`} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 px-2">
                            <p className="font-medium text-gray-800 text-xs">{stock.stock_name}</p>
                            <p className="text-xs text-gray-400">{stock.stock_code}</p>
                          </td>
                          <td className="py-2 px-2 text-right text-xs text-gray-600 tabular-nums">
                            {formatMoney(stock.buy_price)}
                          </td>
                          <td className="py-2 px-2 text-right text-xs font-medium text-gray-800 tabular-nums">
                            {formatMoney(stock.current_price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">오늘 신규 편입 종목 없음</p>
                </div>
              )
            )}

            {/* 매수 로그 */}
            {activeTab === 'log' && (
              todayBuyLog.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">시간</th>
                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">종목</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">수량</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">가격</th>
                        <th className="text-center py-2 px-2 text-xs font-medium text-gray-500">결과</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todayBuyLog.map((log, idx) => (
                        <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 px-2 text-xs text-gray-600 tabular-nums">{log.time}</td>
                          <td className="py-2 px-2">
                            <p className="font-medium text-gray-800 text-xs">{log.stock_name}</p>
                          </td>
                          <td className="py-2 px-2 text-right text-xs text-gray-600 tabular-nums">
                            {log.quantity ? `${log.quantity}주` : '-'}
                          </td>
                          <td className="py-2 px-2 text-right text-xs text-gray-600 tabular-nums">
                            {log.price ? formatMoney(log.price) : '-'}
                          </td>
                          <td className="py-2 px-2 text-center">
                            {log.result === 'success' ? (
                              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">성공</span>
                            ) : log.result === 'skip' ? (
                              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">스킵</span>
                            ) : (
                              <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full" title={log.reason}>실패</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">오늘 매수 로그 없음</p>
                </div>
              )
            )}
          </div>

          {/* 보유 비교 모달 */}
          {showComparison && comparison && (
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm text-gray-500 font-medium">보유종목 비교 (SE vs 우리)</h3>
                <button
                  onClick={() => setShowComparison(false)}
                  className="text-gray-400 hover:text-gray-600 text-xs"
                >
                  닫기
                </button>
              </div>
              <div className="space-y-3">
                {comparison.only_stockeasy?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-orange-600 mb-1">SE에만 있는 종목 ({comparison.only_stockeasy.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {comparison.only_stockeasy.map(s => (
                        <span key={s.stock_code} className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">
                          {s.stock_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {comparison.only_ours?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-blue-600 mb-1">우리만 보유 ({comparison.only_ours.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {comparison.only_ours.map(s => (
                        <span key={s.stock_code} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                          {s.stock_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {comparison.both?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-green-600 mb-1">동일 보유 ({comparison.both.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {comparison.both.map(s => (
                        <span key={s.stock_code} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">
                          {s.stock_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {comparison.only_stockeasy?.length === 0 && comparison.only_ours?.length === 0 && (
                  <p className="text-center text-xs text-gray-400 py-4">보유종목이 완전히 일치합니다</p>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  </CollectingOverlay>
  );
}

export default Strategy;
